import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ELanguage, routesLang } from '../utils';
import { RenderModeService } from './render-mode';
import { Router } from '@angular/router';
import {
  ROUTE_FOOTBALL,
  ROUTE_FOOTBALL_EN,
  ROUTE_FOOTBALL_TEAMS,
  ROUTE_FOOTBALL_TEAMS_EN,
} from '../routes';

type TextForLangParams = Partial<Record<ELanguage, string>>;

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private renderMode = inject(RenderModeService);
  private router = inject(Router);
  readonly defaultLanguage = ELanguage.ES;

  readonly languages = [
    { code: ELanguage.ES, value: 'Spanish', flag: 'es-icon.png' },
    { code: ELanguage.EN, value: 'English', flag: 'en-icon.png' },
  ];

  constructor(public translate: TranslateService) {
    if (this.renderMode.isBrowser()) {
      this.loadServices();
    }
  }

  loadServices() {
    const language: any = localStorage.getItem('language');
    this.translate.setDefaultLang(
      language ? JSON.parse(language).lang : this.defaultLanguage
    );
  }

  setLanguage(lang: ELanguage) {
    localStorage.removeItem('language');
    localStorage.setItem('language', JSON.stringify({ lang }));
    return true;
  }

  useLanguage(lang: any) {
    this.translate.use(lang ? lang : this.translate.getDefaultLang());
  }

  getCurrentLanguage(): ELanguage {
    const language: any = localStorage.getItem('language');
    return language ? JSON.parse(language).lang : this.defaultLanguage;
  }

  getLangLocalStorage() {
    const language: string | null = localStorage.getItem('language');
    if (!language) return this.defaultLanguage;

    const { lang } = JSON.parse(language) as { lang: string };
    return lang;
  }

  getDefaultLanguage() {
    return this.defaultLanguage;
  }

  getTraduccionName(
    traduccionByApi: Record<string, string> | null,
    defaultName: string
  ): string {
    const lang = this.getLangLocalStorage();
    const translation = traduccionByApi?.[lang];
    return translation?.trim() ? translation : defaultName;
  }
  getTraduccionNameFromRoute(
    traduccionByApi: Record<string, string> | null,
    defaultName: string
  ): string {
    const lang = this.getLangLocalStorage();
    const translation = traduccionByApi?.[lang];

    const resp = translation?.trim() ? translation : defaultName;
    return this.normalizeRoute(resp);
  }

  slugifyNameRoute(
    homeTeam: {
      NameTranslation: Record<string, string> | null;
      defaultName: string;
    },
    awayTeam: {
      NameTranslation: Record<string, string> | null;
      defaultName: string;
    }
  ) {
    const home = this.normalizeRoute(
      this.getTraduccionName(homeTeam.NameTranslation, homeTeam.defaultName)
    );

    const away = this.normalizeRoute(
      this.getTraduccionName(awayTeam.NameTranslation, awayTeam.defaultName)
    );

    return `${home}-vs-${away}`;
  }

  getInitPathFootball() {
    const lang = this.getLangLocalStorage();
    const pathInit = lang === 'es' ? ROUTE_FOOTBALL : ROUTE_FOOTBALL_EN;

    return pathInit;
  }

  getInitPathNews() {
    const lang = this.getLangLocalStorage();
    const pathInit = lang === 'es' ? 'noticias' : 'news';

    return pathInit;
  }
  getInitPathFootballMath() {
    const lang = this.getLangLocalStorage();
    const pathInit = lang === 'es' ? 'futbol/partido' : 'en/football/match';

    return pathInit;
  }

  normalizeRoute(route: string) {
    return route
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ñ/g, 'n')
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/--+/g, '-')
      .toLowerCase();
  }

  getTraslateUrlTeamDetails(
    allTextForLangs: Partial<Record<ELanguage, string>>,
    name: string,
    teamId: string
  ): string {
    if (this.getCurrentLanguage() === ELanguage.EN) {
      return `/${ROUTE_FOOTBALL_TEAMS_EN.replace(
        ':slug',
        this.normalizeRoute(this.getTraduccionName(allTextForLangs, name))
      ).replace(':teamId', teamId)}`;
    } else {
      return `/${ROUTE_FOOTBALL_TEAMS.replace(
        ':slug',
        this.normalizeRoute(this.getTraduccionName(allTextForLangs, name))
      ).replace(':teamId', teamId)}`;
    }
  }
}
